import Node from '../node.js';
import { PARAM_TYPE } from '../connector.js';
import Spline from '../spline.js';
import Utils from '../utils.js';
import { CSG } from '../csg2d.js';
import CSG3D from '../../../../csg3d';

const CSG_LIB = CSG3D;

export default class NodeBoolean extends Node
{
	static OP_CHOICES =
	[
		{key: 'add', label: 'Add'},
		{key: 'subtract', label: 'Subtract'},
		{key: 'intersect', label: 'Intersect'},
	]

	constructor(pParent)
	{
		super(pParent);
		this.addInput(PARAM_TYPE.GEOMETRY, 'geo1', null);
		this.addInput(PARAM_TYPE.GEOMETRY, 'geo2', null);

		this.addInput(PARAM_TYPE.DROPDOWN, 'operation', NodeBoolean.OP_CHOICES[0].key, { dropdownOptions: NodeBoolean.OP_CHOICES });
		this.addInput(PARAM_TYPE.BOOLEAN, 'switchInputs', false);

		this.addInput(PARAM_TYPE.BOOLEAN, 'deleteSecondOperand', true);

		this.addOutput(PARAM_TYPE.GEOMETRY, 'geo');
	}

	getType()
	{
		return 'boolean';
	}

	getAllowResultCachingSubclass()
	{
		return true;
	}

	generateSubclass(scene, inputValues, outputName, computedOutputResults)
	{	
		let pGeoList1 = inputValues['geo1'];
		let pGeoList2 = inputValues['geo2'];

		if((!pGeoList1 || pGeoList1.length < 1) && (!pGeoList2 || pGeoList2.length < 1))
			return null;

		if(!pGeoList2 || pGeoList2.length < 1)
			return pGeoList1;

		if(!pGeoList1 || pGeoList1.length < 1)
			return pGeoList2;

		// See whether it's a mesh or one of our paths
		if(Utils.getIsMeshObject(pGeoList1[0]))
			return this.doMeshBoolean(scene, inputValues, outputName, computedOutputResults);
		else if(Utils.getIsSplineObject(pGeoList1))
			return this.doCurveBoolean(scene, inputValues, outputName, computedOutputResults);
		else
			return null;
	}

	doMeshBoolean(scene, inputValues, outputName, computedOutputResults)
	{ 
		let pGeoList1 = inputValues['geo1'];
		let pGeoList2 = inputValues['geo2'];

		let pFirstOperandMeshList;
		let pSecondOperandMeshList;	
		if(inputValues['switchInputs'])
		{
			pFirstOperandMeshList = pGeoList2;
			pSecondOperandMeshList = pGeoList1;
		}
		else
		{
			pFirstOperandMeshList = pGeoList1;
			pSecondOperandMeshList = pGeoList2;			
		}

		let pFinalMeshList = [];
		for(let firstOpIndex = 0; firstOpIndex < pFirstOperandMeshList.length; firstOpIndex++)
		{
			let firstOperandMesh = pFirstOperandMeshList[firstOpIndex];
			if(firstOperandMesh.isDisposed())
				throw new Error(`First operand list contains disposed mesh: ${firstOperandMesh.name}`);

			let geo1CSG = CSG_LIB.CSG.FromMesh(firstOperandMesh);

			// If we're intersecting, we must add up all of our second meshes into one single CSG, otherwise,
			// we're going to do piecewise intersection, which is probably not what we want, since won't
			// give us meaningful results
			let geo2CSG;
			if(inputValues['operation'] == 'intersect')
			{
				let geoTempCSG;
				for(let pMesh of pSecondOperandMeshList)
				{
					if(pMesh.isDisposed())
						throw new Error(`Second operand list contains disposed mesh: ${pMesh.name}`);

					geoTempCSG = CSG_LIB.CSG.FromMesh(pMesh);
					if(geo2CSG)
						geo2CSG.unionInPlace(geoTempCSG);
					else
						geo2CSG = geoTempCSG;
				}

				geo1CSG.intersectInPlace(geo2CSG);
			}
			else
			{
				for(let pMesh of pSecondOperandMeshList)
				{
					if(pMesh.isDisposed())
						throw new Error(`Second operand list contains disposed mesh: ${pMesh.name}`);

					geo2CSG = CSG_LIB.CSG.FromMesh(pMesh);

					if(inputValues['operation'] == 'add')
						geo1CSG.unionInPlace(geo2CSG);
					else if(inputValues['operation'] == 'subtract')
						geo1CSG.subtractInPlace(geo2CSG);
				}
			}
        
			const strMeshName = this.getGeneratedMeshName();
			let newMesh = geo1CSG.toMesh(strMeshName, firstOperandMesh.material, scene);

			if(newMesh.geometry === null)
			{
				newMesh.dispose();
			}
			else
			{
				this.onMeshCreated(newMesh);
				if(firstOperandMesh.parentMaterialNodeId !== undefined)
					newMesh.parentMaterialNodeId = firstOperandMesh.parentMaterialNodeId;
				pFinalMeshList.push(newMesh)
			}
		}

		for(let pMesh of pFirstOperandMeshList)
			pMesh.dispose();

		if(inputValues['deleteSecondOperand'])
		{
			for(let pMesh of pSecondOperandMeshList)
				pMesh.dispose();
		}

		return pFinalMeshList;
	}

	doCurveBoolean(scene, inputValues, outputName, computedOutputResults)
	{
		let pGeoList1 = inputValues['geo1'];
		let pGeoList2 = inputValues['geo2'];

		let pFirstOperandMeshList;
		let pSecondOperandMeshList;	
		if(inputValues['switchInputs'])
		{
			pFirstOperandMeshList = pGeoList2;
			pSecondOperandMeshList = pGeoList1;
		}
		else
		{
			pFirstOperandMeshList = pGeoList1;
			pSecondOperandMeshList = pGeoList2;			
		}

		if(!Array.isArray(pFirstOperandMeshList))
			pFirstOperandMeshList = [pFirstOperandMeshList];
		if(!Array.isArray(pSecondOperandMeshList))
			pSecondOperandMeshList = [pSecondOperandMeshList];

		let pMainSpline = new Spline(pFirstOperandMeshList[0]);
		pMainSpline.ensureOrientation();
		let subjectPolygons = CSG.fromPolygons([pMainSpline.getLinearPoints()]);

		for(let pSplineJson of pSecondOperandMeshList)
		{
			let currSpline = new Spline(pSplineJson);
			currSpline.ensureOrientation();
			let currPolygons = CSG.fromPolygons([currSpline.getLinearPoints()]);

			if(inputValues['operation'] == 'add')
				subjectPolygons = subjectPolygons.union(currPolygons);
			else if(inputValues['operation'] == 'subtract')
				subjectPolygons = subjectPolygons.subtract(currPolygons);
			else if(inputValues['operation'] == 'intersect')
				subjectPolygons = subjectPolygons.intersect(currPolygons);

		}

		subjectPolygons = subjectPolygons.toPolygons();

		// Return a list of linear splines
		let pTempSpline = new Spline();
		let pFinalList = [];
		for(let pPoly of subjectPolygons)
		{
			pTempSpline.setFromLinearPoints(pPoly, false);
			pTempSpline.removeDuplicatePoints();
			pTempSpline.removeCollinearPoints();
			pFinalList.push(pTempSpline.saveToObject());
		}

		return pFinalList[0];
	}
}